import { createSelector } from 'reselect';

import deepEqual from 'deep-equal';

import { appointmentSelectors } from './appointment.selectors';
import { appointmentDraftSelectors } from './appointment-draft.selectors';

const areProductsTheSame = createSelector(
  appointmentSelectors.selectProducts,
  appointmentDraftSelectors.selectProducts,
  (appointmentProducts, draftProducts) =>
    deepEqual(
      appointmentProducts,
      draftProducts.map((product) => ({
        id: product.id,
        information: product.information,
        quantity: product.quantity,
        selectionTypeId: product.selectionType?.id,
      })),
    ),
);

const areAddressesTheSame = createSelector(
  appointmentSelectors.selectAddress,
  appointmentDraftSelectors.selectDraftAddress,
  (appointmentAddress, draftAppointmentAddress) =>
    !appointmentAddress || deepEqual(appointmentAddress, draftAppointmentAddress),
);

const areAppointmentTypeIdsTheSame = createSelector(
  appointmentSelectors.selectAppointment,
  appointmentDraftSelectors.selectAppointmentType,
  appointmentDraftSelectors.selectRelatedAppointmentDrafts,
  (appointment, draftAppointmentType, relatedAppointmentDrafts) => {
    if (appointment) {
      const areRelatedAppointmentTypeIdsTheSame = () =>
        relatedAppointmentDrafts.length === appointment.relatedAppointments.length &&
        relatedAppointmentDrafts.every((addon) =>
          appointment.relatedAppointments.find(
            (el) => el.appointmentType.id === addon.appointmentType.localId,
          ),
        );

      return (
        appointment.appointmentType.id === draftAppointmentType.localId &&
        areRelatedAppointmentTypeIdsTheSame()
      );
    }
    return false;
  },
);

const areAppointmentDateSlotsTheSame = createSelector(
  appointmentSelectors.selectAppointmentDateSlot,
  appointmentDraftSelectors.selectAppointmentDateSlot,
  (appointmentDateSlot, draftAppointmentDateSlot) =>
    appointmentDateSlot === draftAppointmentDateSlot,
);

const areAppointmentTimeSlotsTheSame = createSelector(
  appointmentSelectors.selectAppointmentTimeSlot,
  appointmentDraftSelectors.selectAppointmentTimeSlotStartTime,
  (appointmentTimeSlot, draftAppointmentTimeSlotStartTime) =>
    appointmentTimeSlot === draftAppointmentTimeSlotStartTime,
);

const areAppointmentEventIdsTheSame = createSelector(
  appointmentSelectors.selectEventId,
  appointmentDraftSelectors.selectAppointmentEventId,
  (appointmentEventId, draftAppointmentEventId) => appointmentEventId === draftAppointmentEventId,
);

// might be an useless check because when we change store appointment type id is also changes because it depends on localAppointmentTypeId
const areAppointmentStoreIdsTheSame = createSelector(
  appointmentSelectors.selectStoreId,
  appointmentDraftSelectors.selectShop,
  (appointmentStoreId, draftAppointmentShop) => appointmentStoreId === draftAppointmentShop?.id,
);

const areAppointmentPartnerIdsTheSame = createSelector(
  appointmentSelectors.selectAppointmentPartner,
  appointmentDraftSelectors.selectPartnerId,
  (appointmentPartner, draftAppointmentPartnerId) =>
    !draftAppointmentPartnerId || appointmentPartner?.id === draftAppointmentPartnerId,
);

const areRelatedAppointmentsTheSame = createSelector(
  appointmentSelectors.selectRelatedAppointments,
  appointmentDraftSelectors.selectRelatedAppointmentDrafts,
  (relatedAppointments, addons) =>
    relatedAppointments.length === addons.length &&
    addons.every((addon) =>
      relatedAppointments.find(
        (el) =>
          el.appointmentType.id === addon.appointmentType.localId &&
          el.time === addon.timeSlot.from,
      ),
    ),
);

const areAppointmentsTheSame = createSelector(
  areAppointmentTypeIdsTheSame,
  areAppointmentDateSlotsTheSame,
  areAppointmentTimeSlotsTheSame,
  areAppointmentStoreIdsTheSame,
  areAppointmentPartnerIdsTheSame,
  areProductsTheSame,
  areRelatedAppointmentsTheSame,
  areAddressesTheSame,
  (...conditions) => conditions.every(Boolean),
);

export const appointmentToAppointmentDraftComparisonSelectors = {
  areProductsTheSame,
  areAppointmentTypeIdsTheSame,
  areRelatedAppointmentsTheSame,
  areAppointmentsTheSame,
  areAppointmentStoreIdsTheSame,
  areAppointmentPartnerIdsTheSame,
  areAppointmentDateSlotsTheSame,
  areAppointmentTimeSlotsTheSame,
  areAppointmentEventIdsTheSame,
  areAddressesTheSame,
};
